import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  FileSpreadsheet,
  FileWarning,
  Loader2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { formatNumber } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
// Full-page view — no modal/sheet wrapper
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PreprocessingProgress } from "@/components/preprocessing-progress";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

/** Wire identifiers, not copy — the words come from `targetFields`. Module-level so the linter
 * reads them as the field names they are (POS2-1134 CP5d). */
const DEFAULTABLE_FIELDS = ["supplier", "currency", "pack_unit", "content_unit"];

// ── Types ──────────────────────────────────────────────────────────────

interface TargetField {
  key: string;
  labelKey: string;
  required?: boolean;
}

interface HeaderMapping {
  csv_header: string;
  target_field: string | null;
  confidence: number;
}

interface PreviewRow {
  row: number;
  valid: boolean;
  data: Record<string, string | null>;
  errors: string[];
}

interface ValidationResult {
  total_rows: number;
  valid_count: number;
  error_count: number;
  rows: PreviewRow[];
  error_groups: Record<string, number[]> | null;
}

interface AIPreviewItem {
  raw: string;
  clean_name: string;
  is_food: boolean;
  category: string;
  subcategory: string;
  storage_type: string;
  price_per_kg: number | null;
  shelf_life_days: number | null;
  allergens: string[];
}

interface SmartImportWizardProps {
  listId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  targetFields?: TargetField[];
  queryKeysToInvalidate?: string[][];
  // Fired after a successful import (in addition to the built-in toast) so the
  // caller can nudge follow-up actions, e.g. re-processing menus (POS2-731).
  onImported?: () => void;
}

const INVENTORY_FIELDS: TargetField[] = [
  // Core
  { key: "description", labelKey: "settings.importFieldDescription", required: true },
  { key: "sku", labelKey: "settings.importFieldSku" },
  { key: "supplier", labelKey: "settings.importFieldSupplier" },
  { key: "price", labelKey: "settings.importFieldPrice" },
  { key: "currency", labelKey: "settings.importFieldCurrency" },
  { key: "price_per_kg", labelKey: "settings.importFieldPricePerKg" },
  { key: "unit_pack_size", labelKey: "settings.importFieldUnitPackSize" },
  // Pack structure
  { key: "pack_quantity", labelKey: "settings.importFieldPackQuantity" },
  { key: "pack_unit", labelKey: "settings.importFieldPackUnit" },
  { key: "content_quantity", labelKey: "settings.importFieldContentQuantity" },
  { key: "content_unit", labelKey: "settings.importFieldContentUnit" },
  { key: "recipe_unit", labelKey: "settings.importFieldRecipeUnit" },
  // Supplier metadata
  { key: "manufacturer_code", labelKey: "settings.importFieldManufacturerCode" },
  { key: "ean", labelKey: "settings.importFieldEan" },
  { key: "supplier_category", labelKey: "settings.importFieldSupplierCategory" },
  { key: "vat_rate", labelKey: "settings.importFieldVatRate" },
  { key: "price_valid_from", labelKey: "settings.importFieldPriceValidFrom" },
  { key: "price_valid_until", labelKey: "settings.importFieldPriceValidUntil" },
  // Nutrition
  { key: "energy_kcal", labelKey: "settings.importFieldEnergyKcal" },
  { key: "energy_kj", labelKey: "settings.importFieldEnergyKj" },
  { key: "protein_g", labelKey: "settings.importFieldProteinG" },
  { key: "fat_g", labelKey: "settings.importFieldFatG" },
  { key: "saturated_fat_g", labelKey: "settings.importFieldSaturatedFatG" },
  { key: "carbohydrates_g", labelKey: "settings.importFieldCarbohydratesG" },
  { key: "fibre_g", labelKey: "settings.importFieldFibreG" },
  { key: "sodium_mg", labelKey: "settings.importFieldSodiumMg" },
  // Other
  { key: "typical_shelf_life_days", labelKey: "settings.importFieldTypicalShelfLifeDays" },
  { key: "typical_weight_kg", labelKey: "settings.importFieldTypicalWeightKg" },
  { key: "edible_yield_pct", labelKey: "settings.importFieldEdibleYieldPct" },
  { key: "allergens", labelKey: "settings.importFieldAllergens" },
  { key: "aliases", labelKey: "settings.importFieldAliases" },
  { key: "status", labelKey: "settings.importFieldStatus" },
];

// ── Mapping groups ──────────────────────────────────────────────────────

const MAPPING_GROUPS = [
  {
    labelKey: "settings.importGroupProductInfo",
    keys: new Set(["description", "sku", "supplier", "aliases", "status", "supplier_category"]),
    defaultOpen: true,
  },
  {
    labelKey: "settings.importGroupPackUnits",
    keys: new Set([
      "pack_quantity",
      "pack_unit",
      "content_quantity",
      "content_unit",
      "recipe_unit",
      "unit_pack_size",
    ]),
    defaultOpen: true,
  },
  {
    labelKey: "settings.importGroupPricing",
    keys: new Set([
      "price",
      "currency",
      "price_per_kg",
      "vat_rate",
      "price_valid_from",
      "price_valid_until",
    ]),
    defaultOpen: true,
  },
  {
    labelKey: "settings.importGroupSupplierMetadata",
    keys: new Set(["manufacturer_code", "ean"]),
    defaultOpen: false,
  },
  {
    labelKey: "settings.importGroupPhysicalProperties",
    keys: new Set([
      "typical_shelf_life_days",
      "typical_weight_kg",
      "edible_yield_pct",
      "allergens",
    ]),
    defaultOpen: false,
  },
  {
    labelKey: "settings.importGroupNutritionalData",
    keys: new Set([
      "energy_kcal",
      "energy_kj",
      "protein_g",
      "fat_g",
      "saturated_fat_g",
      "carbohydrates_g",
      "fibre_g",
      "sodium_mg",
    ]),
    defaultOpen: false,
  },
];

// ── Helpers ─────────────────────────────────────────────────────────────

function parseCSV(text: string): { headers: string[]; rows: string[][]; totalRows: number } {
  const lines = text.split("\n").filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], rows: [], totalRows: 0 };
  const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
  const rows = lines
    .slice(1)
    .map((line) => line.split(",").map((c) => c.trim().replace(/^"|"$/g, "")));
  return { headers, rows, totalRows: rows.length };
}

// ── Component ──────────────────────────────────────────────────────────

export function SmartImportWizard({
  listId,
  open,
  onOpenChange,
  targetFields = INVENTORY_FIELDS,
  queryKeysToInvalidate = [
    ["inventory-items"],
    ["inventory-lists"],
    ["admin", "inventory-items"],
    ["admin", "inventory-lists"],
  ],
  onImported,
}: SmartImportWizardProps) {
  const { locale, t } = useTranslation();
  const apiHeaders = useApiHeaders();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [file, setFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<string[][]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [mappings, setMappings] = useState<HeaderMapping[]>([]);
  const [aiMapped, setAiMapped] = useState<Set<string>>(new Set());
  const [defaults, setDefaults] = useState<Record<string, string>>({});
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [aiPreview, setAiPreview] = useState<AIPreviewItem[] | null>(null);
  const [importing, setImporting] = useState(false);
  const [importComplete, setImportComplete] = useState(false);

  function reset() {
    setStep(1);
    setFile(null);
    setCsvHeaders([]);
    setSampleRows([]);
    setTotalRows(0);
    setMappings([]);
    setAiMapped(new Set());
    setDefaults({});
    setValidation(null);
    setAiPreview(null);
    setImporting(false);
    setImportComplete(false);
  }

  // ── Step 1: Upload & Map ────────────────────────────────────────────

  const mapMutation = useMutation({
    mutationFn: async (parsed: { headers: string[]; sampleRows: string[][] }) => {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/import/map-headers`, {
        method: "POST",
        headers: apiHeaders,
        body: JSON.stringify({ headers: parsed.headers, sample_rows: parsed.sampleRows }),
      });
      if (!res.ok) throw new Error("Mapping failed");
      return (await res.json()).data as HeaderMapping[];
    },
    onSuccess: (data) => {
      setMappings(data);
      setAiMapped(new Set(data.filter((m) => m.target_field).map((m) => m.csv_header)));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const text = await f.text();
    const parsed = parseCSV(text);
    setCsvHeaders(parsed.headers);
    setSampleRows(parsed.rows.slice(0, 3));
    setTotalRows(parsed.totalRows);
    mapMutation.mutate({ headers: parsed.headers, sampleRows: parsed.rows.slice(0, 5) });
  }

  function updateMapping(csvHeader: string, targetField: string | null) {
    setMappings((prev) =>
      prev.map((m) =>
        m.csv_header === csvHeader ? { ...m, target_field: targetField, confidence: 1 } : m,
      ),
    );
    setAiMapped((prev) => {
      const next = new Set(prev);
      next.delete(csvHeader);
      return next;
    });
  }

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    const closed = new Set<string>();
    for (const g of MAPPING_GROUPS) {
      if (!g.defaultOpen) closed.add(g.labelKey);
    }
    return closed;
  });

  const usedTargets = new Set(mappings.filter((m) => m.target_field).map((m) => m.target_field));
  const descriptionMapped = mappings.some((m) => m.target_field === "description");
  const mappedCount = mappings.filter((m) => m.target_field).length;

  // ── Step 2: Validate ────────────────────────────────────────────────

  function buildCompactMapping(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const m of mappings) {
      if (m.target_field) result[m.csv_header] = m.target_field;
    }
    return result;
  }

  const validateMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("No file");
      const form = new FormData();
      form.append("file", file);
      const h: Record<string, string> = {};
      for (const [k, v] of Object.entries(apiHeaders)) {
        if (k !== "Content-Type") h[k] = v;
      }
      const mapping = encodeURIComponent(JSON.stringify(buildCompactMapping()));
      let url = `${API_URL}/api/v1/inventory-lists/${listId}/import?preview=true&column_mapping=${mapping}`;
      if (Object.keys(defaults).length > 0) {
        url += `&defaults=${encodeURIComponent(JSON.stringify(defaults))}`;
      }
      const res = await fetch(url, { method: "POST", headers: h, body: form });
      if (!res.ok) throw new Error("Validation failed");
      return (await res.json()).data as ValidationResult;
    },
    onSuccess: (data) => {
      setValidation(data);
      setStep(2);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ── Step 3: AI Preview & Import ─────────────────────────────────────

  const previewProcessMutation = useMutation({
    mutationFn: async () => {
      if (!validation) throw new Error("No validation data");
      const validRows = validation.rows.filter((r) => r.valid).slice(0, 20);
      const res = await fetch(
        `${API_URL}/api/v1/inventory-lists/${listId}/import/preview-process`,
        {
          method: "POST",
          headers: apiHeaders,
          body: JSON.stringify({ rows: validRows.map((r) => r.data) }),
        },
      );
      if (!res.ok) throw new Error("Preview processing failed");
      return (await res.json()).data as AIPreviewItem[];
    },
    onSuccess: (data) => {
      setAiPreview(data);
      setStep(3);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const importMutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("No file");
      setImporting(true);
      const form = new FormData();
      form.append("file", file);
      const h: Record<string, string> = {};
      for (const [k, v] of Object.entries(apiHeaders)) {
        if (k !== "Content-Type") h[k] = v;
      }
      const mapping = encodeURIComponent(JSON.stringify(buildCompactMapping()));
      let url = `${API_URL}/api/v1/inventory-lists/${listId}/import?column_mapping=${mapping}`;
      if (Object.keys(defaults).length > 0) {
        url += `&defaults=${encodeURIComponent(JSON.stringify(defaults))}`;
      }
      const res = await fetch(url, { method: "POST", headers: h, body: form });
      if (!res.ok) throw new Error("Import failed");
      return (await res.json()).data;
    },
    onSuccess: (data) => {
      setImporting(false);
      setImportComplete(true);
      for (const key of queryKeysToInvalidate) queryClient.invalidateQueries({ queryKey: key });
      toast.success(t("settings.itemsImported", { count: data.success_count }));
      onImported?.();
    },
    onError: (err: Error) => {
      setImporting(false);
      toast.error(err.message);
    },
  });

  // ── Render ──────────────────────────────────────────────────────────

  const stepLabels = ["Map", "Validate", "Process"];

  if (!open) return null;

  return (
    <div className="absolute inset-0 flex flex-col bg-gray-50 p-6">
      <div className="pb-4">
        <h1 className="text-lg font-medium">{t("settings.importCatalogue")}</h1>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-3 border-b border-gray-200 pb-3">
        {stepLabels.map((label, i) => (
          <div key={label} className="flex items-center gap-3">
            {i > 0 && <div className="h-px w-8 bg-gray-200" />}
            <div
              className={`flex items-center gap-1.5 text-xs ${step === i + 1 ? "font-medium text-gray-900" : "text-gray-400"}`}
            >
              <span
                className={`flex size-5 items-center justify-center rounded-full text-[10px] font-medium ${step === i + 1 ? "bg-gray-900 text-white" : step > i + 1 ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-400"}`}
              >
                {step > i + 1 ? "\u2713" : i + 1}
              </span>
              {label}
            </div>
          </div>
        ))}
      </div>

      {/* Content — scrollable */}
      <div className="min-h-0 flex-1 overflow-auto py-4">
        {/* ── Step 1: Upload & Map ──────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-4">
            {/* File upload — compact bar when file loaded, dropzone when empty */}
            {file && mappings.length > 0 ? (
              <div className="flex items-center gap-3 rounded-lg border border-gray-200 px-3 py-2">
                <FileSpreadsheet className="size-4 text-gray-400 shrink-0" />
                <span className="text-sm font-medium text-gray-900 truncate">{file.name}</span>
                <span className="text-xs text-gray-400">
                  {t("settings.rowsColumns", {
                    rows: formatNumber(locale, totalRows),
                    columns: csvHeaders.length,
                  })}
                </span>
                <button
                  type="button"
                  className="ml-auto text-xs text-gray-400 hover:text-gray-600"
                  onClick={() => fileRef.current?.click()}
                >
                  {t("settings.replaceFile")}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFile}
                  className="hidden"
                />
              </div>
            ) : (
              <div className="relative">
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFile}
                  className="absolute inset-0 cursor-pointer opacity-0"
                />
                <div className="flex items-center justify-center rounded-lg border-2 border-dashed border-gray-200 px-6 py-5 text-center hover:border-gray-300">
                  <div className="flex items-center gap-3">
                    <Upload className="size-5 text-gray-400" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-700">
                        {t("settings.chooseACsvFile")}
                      </p>
                      <p className="text-xs text-gray-400">{t("settings.anyLanguageAnyFormat")}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {mapMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="size-4 animate-spin" />
                {t("settings.mappingWithAi")}
              </div>
            )}

            {/* Grouped column mappings */}
            {mappings.length > 0 &&
              MAPPING_GROUPS.map((group) => {
                const groupMappings = mappings.filter((m) => {
                  const target = m.target_field;
                  if (target && group.keys.has(target)) return true;
                  if (!target) {
                    // Unmapped columns: put in first group that hasn't claimed them
                    const csvIdx = csvHeaders.indexOf(m.csv_header);
                    return csvIdx >= 0 && csvIdx < 6 && group === MAPPING_GROUPS[0];
                  }
                  return false;
                });
                // Also include unmapped columns not claimed by any group — put in Product Info
                const allClaimed = new Set(
                  MAPPING_GROUPS.flatMap((g) =>
                    mappings
                      .filter((m) => m.target_field && g.keys.has(m.target_field))
                      .map((m) => m.csv_header),
                  ),
                );
                const unclaimed =
                  group === MAPPING_GROUPS[0]
                    ? mappings.filter((m) => !m.target_field && !allClaimed.has(m.csv_header))
                    : [];
                const allInGroup = [...groupMappings, ...unclaimed];

                if (allInGroup.length === 0) return null;

                const isCollapsed = collapsedGroups.has(group.labelKey);
                const groupMapped = allInGroup.filter((m) => m.target_field).length;

                return (
                  <div key={group.labelKey}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 py-1.5 text-left"
                      onClick={() =>
                        setCollapsedGroups((prev) => {
                          const next = new Set(prev);
                          if (next.has(group.labelKey)) next.delete(group.labelKey);
                          else next.add(group.labelKey);
                          return next;
                        })
                      }
                    >
                      <ChevronDown
                        className={`size-3.5 text-gray-400 transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                      />
                      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {t(group.labelKey)}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {t("settings.groupMapped", {
                          mapped: groupMapped,
                          total: allInGroup.length,
                        })}
                      </span>
                    </button>
                    {!isCollapsed && (
                      <div className="divide-y divide-gray-100">
                        {allInGroup.map((m) => {
                          const idx = csvHeaders.indexOf(m.csv_header);
                          const sample = sampleRows
                            .map((r) => r[idx])
                            .filter(Boolean)
                            .slice(0, 2)
                            .join(", ");
                          const isAi = aiMapped.has(m.csv_header);
                          const truncatedSample =
                            sample.length > 40 ? sample.slice(0, 40) + "\u2026" : sample;
                          return (
                            <div
                              key={m.csv_header}
                              className="grid grid-cols-[28%_32%_1fr] items-center gap-2 h-11 px-1"
                            >
                              <span className="text-sm text-gray-900 truncate">{m.csv_header}</span>
                              <div className="flex items-center gap-1.5">
                                {isAi && m.target_field && (
                                  <span
                                    className="size-1.5 rounded-full bg-emerald-500 shrink-0"
                                    title={t("settings.aiSuggested")}
                                  />
                                )}
                                <Select
                                  value={m.target_field ?? "_skip"}
                                  onValueChange={(v) =>
                                    updateMapping(m.csv_header, v === "_skip" ? null : v)
                                  }
                                >
                                  <SelectTrigger className="h-7 text-xs border-0 shadow-none hover:bg-gray-50 px-2">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="_skip">
                                      {t("settings.skipColumn")}
                                    </SelectItem>
                                    {targetFields.map((f) => (
                                      <SelectItem
                                        key={f.key}
                                        value={f.key}
                                        disabled={
                                          usedTargets.has(f.key) && m.target_field !== f.key
                                        }
                                      >
                                        {t(f.labelKey)}
                                        {f.required ? " *" : ""}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <span
                                className="text-xs text-gray-400 font-mono truncate"
                                title={sample || undefined}
                              >
                                {truncatedSample || "\u2014"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

            {/* Default values */}
            {mappings.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t("settings.defaultValuesHeading")}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {DEFAULTABLE_FIELDS.map((field) => {
                    const labelKey = targetFields.find((f) => f.key === field)?.labelKey;
                    const label = labelKey ? t(labelKey) : field;
                    const isMapped = usedTargets.has(field);
                    return (
                      <div key={field}>
                        <label className="text-[11px] text-gray-400">{label}</label>
                        <Input
                          value={defaults[field] ?? ""}
                          onChange={(e) =>
                            setDefaults((prev) => ({
                              ...prev,
                              [field]: e.target.value,
                            }))
                          }
                          placeholder={
                            isMapped
                              ? t("settings.mappedFromCsv")
                              : t("settings.defaultField", { field: label })
                          }
                          disabled={isMapped}
                          className="h-7 text-xs"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Step 2: Validate ──────────────────────────────────── */}
        {step === 2 && validation && (
          <div className="space-y-5">
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                  {t("settings.totalRows")}
                </p>
                <p className="mt-1 text-2xl font-semibold text-gray-900">
                  {formatNumber(locale, validation.total_rows)}
                </p>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                  {t("settings.valid")}
                </p>
                <p className="mt-1 text-2xl font-semibold text-emerald-700">
                  {formatNumber(locale, validation.valid_count)}
                </p>
                <p className="text-[10px] text-gray-400">{t("settings.readyToImport")}</p>
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
                  {t("settings.skipped")}
                </p>
                <p className="mt-1 text-2xl font-semibold text-gray-400">
                  {formatNumber(locale, validation.error_count)}
                </p>
                <p className="text-[10px] text-gray-400">{t("settings.willNotBeImported")}</p>
              </div>
            </div>

            {/* Grouped errors */}
            {validation.error_groups && Object.keys(validation.error_groups).length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {t("settings.issues")}
                </p>
                {Object.entries(validation.error_groups).map(([error, rowNums]) => (
                  <div key={error} className="flex items-center gap-2 rounded bg-red-50 px-3 py-2">
                    <FileWarning className="size-3.5 text-red-400 shrink-0" />
                    <p className="text-xs text-red-700">
                      {t("common.rows", { count: rowNums.length })}: {error}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {validation.error_count === 0 && (
              <div className="flex items-center gap-2 rounded bg-emerald-50 px-3 py-2">
                <CheckCircle2 className="size-3.5 text-emerald-500" />
                <p className="text-xs text-emerald-700">{t("settings.allRowsValid")}</p>
              </div>
            )}
          </div>
        )}

        {/* ── Step 3: AI Preview & Process ──────────────────────── */}
        {step === 3 && (
          <div className="space-y-4">
            {aiPreview && !importComplete && (
              <>
                <p className="text-sm text-gray-500">{t("settings.aiSampleNote")}</p>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs min-w-[180px]">
                          {t("settings.columnRawName")}
                        </TableHead>
                        <TableHead className="text-xs min-w-[180px]">
                          {t("settings.columnCleanName")}
                        </TableHead>
                        <TableHead className="text-xs w-16">{t("settings.columnType")}</TableHead>
                        <TableHead className="text-xs min-w-[100px]">
                          {t("settings.columnCategory")}
                        </TableHead>
                        <TableHead className="text-xs min-w-[100px]">
                          {t("settings.columnStorage")}
                        </TableHead>
                        <TableHead className="text-xs w-16">{t("settings.columnPerKg")}</TableHead>
                        <TableHead className="text-xs w-14">{t("settings.columnDays")}</TableHead>
                        <TableHead className="text-xs min-w-[80px]">
                          {t("settings.columnAllergens")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {aiPreview.map((item, i) => (
                        <TableRow key={i}>
                          <TableCell className="py-1.5 text-xs text-gray-400 truncate max-w-[180px]">
                            {item.raw}
                          </TableCell>
                          <TableCell className="py-1.5 text-xs font-medium truncate max-w-[180px]">
                            {item.clean_name}
                          </TableCell>
                          <TableCell className="py-1.5">
                            <Badge
                              className={`border-0 text-[10px] ${item.is_food ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}
                            >
                              {item.is_food ? t("settings.food") : t("settings.nonFood")}
                            </Badge>
                          </TableCell>
                          <TableCell className="py-1.5 text-xs text-gray-500 truncate">
                            {item.subcategory || item.category || "\u2014"}
                          </TableCell>
                          <TableCell className="py-1.5 text-xs text-gray-500">
                            {item.storage_type || "\u2014"}
                          </TableCell>
                          <TableCell className="py-1.5 text-xs text-gray-500">
                            {item.price_per_kg != null ? item.price_per_kg.toFixed(2) : "\u2014"}
                          </TableCell>
                          <TableCell className="py-1.5 text-xs text-gray-500">
                            {item.shelf_life_days ?? "\u2014"}
                          </TableCell>
                          <TableCell className="py-1.5 text-xs text-gray-400">
                            {item.allergens?.length > 0 ? item.allergens.join(", ") : "\u2014"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}

            {importing && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="size-4 animate-spin" />
                {t("settings.importingItems", {
                  count: formatNumber(locale, validation?.valid_count ?? 0),
                })}
              </div>
            )}

            {importComplete && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-emerald-700">
                  <CheckCircle2 className="size-4" />
                  {t("settings.importCompleteProcessing")}
                </div>
                <div className="rounded-lg border border-gray-200 p-4">
                  <PreprocessingProgress listId={listId} />
                </div>
              </div>
            )}

            {previewProcessMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="size-4 animate-spin" />
                {t("settings.processingSampleWithAi")}
              </div>
            )}
          </div>
        )}

        {validateMutation.isPending && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="size-4 animate-spin" />
            {t("settings.validatingRows", { count: formatNumber(locale, totalRows) })}
          </div>
        )}
      </div>

      {/* Footer — fixed to bottom */}
      <div className="-mx-6 -mb-6 shrink-0">
        <div className="mx-2 h-px bg-gray-200" />
        <div className="flex h-11 items-center justify-between px-6 bg-white">
          <div className="flex items-center gap-3">
            {step === 1 && mappings.length > 0 && (
              <span className="text-xs text-gray-400">
                {t("settings.columnsMapped", {
                  mapped: mappedCount,
                  total: mappings.length,
                })}
              </span>
            )}
            {step > 1 && !importing && !importComplete && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStep((s) => (s - 1) as 1 | 2);
                  if (step === 3) setAiPreview(null);
                }}
              >
                <ArrowLeft className="mr-1.5 size-4" />
                {t("settings.back")}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 1 && mappings.length > 0 && (
              <Button
                size="sm"
                onClick={() => validateMutation.mutate()}
                disabled={!descriptionMapped || validateMutation.isPending}
              >
                {validateMutation.isPending ? t("settings.validating") : t("settings.validate")}
              </Button>
            )}
            {step === 2 && validation && validation.valid_count > 0 && (
              <Button
                size="sm"
                onClick={() => previewProcessMutation.mutate()}
                disabled={previewProcessMutation.isPending}
              >
                {previewProcessMutation.isPending
                  ? t("settings.processing")
                  : t("settings.previewAiProcessing")}
              </Button>
            )}
            {step === 3 && aiPreview && !importing && !importComplete && (
              <Button
                size="sm"
                onClick={() => importMutation.mutate()}
                disabled={importMutation.isPending}
              >
                {t("settings.importItemsButton", {
                  count: formatNumber(locale, validation?.valid_count ?? 0),
                })}
              </Button>
            )}
            {importComplete && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  reset();
                }}
              >
                {t("waste.done")}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
